import { memo } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { AppState } from "@Store/index";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import config from "@Config/index";
import {
  getRadiusDistanceDisplay,
  isLocalityGeo,
  replaceHardcodedRadiusUnit,
} from "@Pages/Search/utils/geoRadiusExpand";
import { getLocalityLabel, getUniversityLabel } from "@Pages/Search/utils/getFilterLabel";
import styles from "./nearbyResultsHeading.module.scss";

type Props = {
  count: number;
  className?: string;
  isDesktop?: boolean;
};

const NearbyResultsHeading = ({ count, className, isDesktop = false }: Props) => {
  const { t } = useTranslationHook();
  const { region, filters, regionRadius } = useSelector(
    ({ searchPageData }: AppState) => ({
      region: searchPageData.region,
      filters: searchPageData.filters,
      regionRadius: searchPageData.dynamicFilterCountData?.data?.data?.region_radius,
    }),
    shallowEqual,
  );

  const placeName = isLocalityGeo(filters, region)
    ? getLocalityLabel(filters) || region?.name || ""
    : getUniversityLabel(filters) || region?.name || "";
  const useMiles = !config.IS_CHINA_APP;
  const { distance, unitKey } = getRadiusDistanceDisplay(regionRadius, useMiles);
  const distanceUnit = t(unitKey);

  return (
    <div
      className={formatClassNames(styles.root, isDesktop && styles.desktop, className)}
      data-testid="radius-expand-nearby-heading"
    >
      <h2 className={styles.title} data-testid="radius-expand-nearby-count">
        {t("searchpage.searchlist.radiusexpand.nearbyheading", { count: String(count) })}
      </h2>
      <p className={styles.subtitle}>
        {replaceHardcodedRadiusUnit(
          t("searchpage.searchlist.radiusexpand.nearbysubtitle", {
            distance,
            unit: distanceUnit,
            name: placeName,
          }),
          distanceUnit,
          useMiles,
        )}
      </p>
    </div>
  );
};

export default wrapperHOC(memo(NearbyResultsHeading), {
  componentName: "NearbyResultsHeading-SearchList-SearchPage",
  showForChina: true,
});
